/**
 * React Router v5 shim providing the same surface area as the v6 router-compat
 * shim in the handoff project.
 *
 * In the handoff (v6) the shim wrapped useNavigate() to expose a v5-like
 * useHistory() API. Here in target (genuinely v5) we just re-export useHistory
 * and add a tiny `navigate` adapter so code written against the v6 shim
 * continues to work.
 *
 * Consumers should `import { useHistory } from 'utils/router-compat'`.
 */
import { useHistory as useHistoryNative, useLocation } from 'react-router-dom'
import type * as History from 'history'

interface LocationDescriptor {
  pathname: string
  state?: unknown
  search?: string
  hash?: string
}

interface NavigateOptions {
  state?: unknown
  replace?: boolean
}

function isLocationDescriptor(path: unknown): path is LocationDescriptor {
  return typeof path === 'object' && path !== null && 'pathname' in (path as Record<string, unknown>)
}

interface CompatHistory {
  push: (path: History.LocationDescriptor | string, state?: unknown) => void
  replace: (path: History.LocationDescriptor | string, state?: unknown) => void
  goBack: () => void
  go: (n: number) => void
  navigate: (to: string | LocationDescriptor | number, opts?: NavigateOptions) => void
  location: ReturnType<typeof useLocation>
}

export function useHistory(): CompatHistory {
  const history = useHistoryNative<unknown>()
  const location = useLocation()
  return {
    push: history.push.bind(history),
    replace: history.replace.bind(history),
    goBack: () => history.goBack(),
    go: (n: number) => history.go(n),
    navigate: (to, opts) => {
      if (typeof to === 'number') {
        history.go(to)
        return
      }
      if (isLocationDescriptor(to)) {
        const { pathname, state, search, hash } = to
        const target = { pathname, search: search ?? '', hash: hash ?? '' } as any
        if (opts?.replace) history.replace(target, opts?.state ?? state)
        else history.push(target, opts?.state ?? state)
        return
      }
      if (opts?.replace) history.replace(to, opts?.state)
      else history.push(to, opts?.state)
    },
    location,
  }
}
